<template>
  <div class="page boss-page growth-page">
    <van-nav-bar :title="employeeId ? ((employeeName || '员工') + '能力画像') : '我的能力画像'" left-arrow @click-left="$router.back()"/>
    <section class="profile-panel">
      <header class="section-head">
        <h2>个性化评分画像</h2>
        <p class="section-desc">基于历史日志评分与主管复核，提炼写作特征与薄弱维度</p>
      </header>
      <div class="profile-metrics">
        <div class="metric-item">
          <span>写作特征</span>
          <b>{{ field(profile, 'writingStyle') || '样本不足' }}</b>
        </div>
        <div class="metric-item">
          <span>主管校准偏好</span>
          <b>{{ field(profile, 'supervisorPreference') || '暂无' }}</b>
        </div>
        <div class="metric-item highlight">
          <span>有效评分样本</span>
          <b>{{ field(profile, 'sampleCount') || 0 }}<em>条</em></b>
        </div>
      </div>
      <div v-if="weakDimensions.length" class="weak-block">
        <h3>薄弱维度</h3>
        <div class="weak-list">
          <div v-for="w in weakDimensions" :key="field(w, 'code')" class="weak-item">
            <b>{{ weakDimensionName(w) }}</b>
            <span>最低 {{ field(w, 'minScore') }} 分 · 出现 {{ field(w, 'hitCount') }} 次</span>
          </div>
        </div>
      </div>
      <van-button class="refresh-btn" block plain type="danger" size="small" :loading="refreshing" @click="refreshProfile">
        重新计算画像
      </van-button>
    </section>
    <section><h2>岗位能力雷达</h2>
      <div v-for="r in radar" :key="field(r, 'capability_code')" class="cap">
        <header><b>{{ field(r, 'capability_name') }}</b><span>{{ field(r, 'assessed_level') }} / {{ field(r, 'target_level') }}</span></header>
        <div><i :style="{width: percent(r) + '%'}"></i></div>
        <p>{{ field(r, 'evidence_criteria') }}</p></div>
      <van-empty v-if="!radar.length" description="岗位能力标准尚未配置"/>
    </section>
    <section><h2>成长推荐</h2>
      <article v-for="r in recommendations" :key="field(r, 'id')"><b>{{ field(r, 'title') }}</b><em>{{
          typeText(field(r, 'recommendation_type'))
        }}</em>
        <p>{{ field(r, 'description') }}</p></article>
      <van-empty v-if="!recommendations.length" description="暂无知识、案例、训练、导师或 Skill 推荐"/>
    </section>
    <section><h2>成长效果</h2>
      <article v-for="r in executions" :key="field(r, 'id')"><b>{{ field(r, 'skill_code') }}</b>
        <p>质量变化 {{ field(r, 'quality_delta') || 0 }}；效率变化 {{ field(r, 'efficiency_delta') || 0 }}</p>
        <small>{{ field(r, 'actual_result') || '实际结果待回收' }}</small></article>
      <van-empty v-if="!executions.length" description="暂无已验证的 Skill 执行效果"/>
    </section>
  </div>
</template>
<script>
import api from '@/utils/coreapi';
import { aiDimensionLabel } from '@/utils/aiDimensionLabels';

export default {
  data() {
    return {profile: {}, radar: [], recommendations: [], executions: [], refreshing: false};
  },
  computed: {
    employeeId() {
      const value = Number(this.$route.query.employeeId);
      return Number.isFinite(value) && value > 0 ? value : null;
    },
    employeeName() {
      return this.$route.query.employeeName || '';
    },
    weakDimensions() {
      return this.field(this.profile, 'weakDimensions') || [];
    }
  },
  created() {
    this.load();
  },
  methods: {
    field(row, key) {
      if (!row) return '';
      return row[key] !== undefined ? row[key] : row[key.toUpperCase()];
    },
    percent(row) {
      const target = Number(this.field(row, 'target_level') || 100), value = Number(this.field(row, 'assessed_level') || 0);
      return Math.min(100, Math.round(value * 100 / target));
    },
    typeText(value) {
      return ({KNOWLEDGE: '知识', CASE: '案例', TRAINING: '训练任务', MENTOR: '导师', SKILL: '执行 Skill'})[value] || value;
    },
    weakDimensionName(item) {
      return aiDimensionLabel(this.field(item, 'code'), this.field(item, 'name'));
    },
    async load() {
      try {
        [this.profile, this.radar, this.recommendations, this.executions] = await Promise.all([api.learningProfile(this.employeeId), api.capabilityRadar(this.employeeId), api.growthRecommendations(this.employeeId), api.skillExecutions(this.employeeId)]);
      } catch (e) {
        this.$toast.fail(e.message);
      }
    },
    async refreshProfile() {
      this.refreshing = true;
      try {
        this.profile = await api.refreshLearningProfile(this.employeeId);
        this.$toast.success('画像已重新计算');
      } catch (e) {
        this.$toast.fail(e.message);
      } finally {
        this.refreshing = false;
      }
    }
  }
};
</script>
<style lang="less" scoped>
@import '../boss/boss.less';
@import '~@/assets/base.less';

.page {
  min-height: 100vh;
  padding-bottom: 1rem;
  background: #f6f7fb;
  color: #34364a;
}

section {
  margin: .26rem;
  padding: .32rem;
  background: #fff;
  border-radius: .18rem;
  box-shadow: 0 .05rem .2rem rgba(30, 35, 60, .06);
}

h2 {
  margin: 0 0 .22rem;
  font-size: .34rem;
  color: #30313a;
}

section > p {
  color: #777b8d;
  font-size: .25rem;
  line-height: 1.5;
}

.profile-panel {
  position: relative;
  overflow: hidden;
}

.profile-panel::before {
  content: '';
  position: absolute;
  left: 0;
  top: .28rem;
  width: .06rem;
  height: .36rem;
  border-radius: 0 .04rem .04rem 0;
  background: @color;
}

.section-head {
  padding-left: .12rem;
}

.section-desc {
  margin: 0;
  color: #9295a7;
  font-size: .24rem;
  line-height: 1.55;
}

.profile-metrics {
  margin-top: .22rem;
  display: grid;
  gap: .12rem;
}

.metric-item {
  padding: .18rem .2rem;
  display: grid;
  grid-template-columns: 1.8rem 1fr;
  align-items: center;
  gap: .12rem;
  background: #f8f9fc;
  border: 1PX solid #eceef3;
  border-radius: .12rem;
}

.metric-item span {
  color: #777b8d;
  font-size: .24rem;
}

.metric-item b {
  color: #34364a;
  font-size: .27rem;
  font-weight: 600;
  line-height: 1.45;
  word-break: break-word;
}

.metric-item.highlight {
  background: #fff5f5;
  border-color: #f3dede;
}

.metric-item.highlight span {
  color: #9a4a4a;
}

.metric-item.highlight b {
  color: @color;
  font-size: .34rem;
}

.metric-item.highlight b em {
  margin-left: .04rem;
  color: #b45309;
  font-size: .22rem;
  font-style: normal;
  font-weight: 500;
}

.weak-block {
  margin-top: .22rem;
  padding-top: .18rem;
  border-top: 1PX solid #f0f1f5;
}

.weak-block h3 {
  margin: 0 0 .12rem;
  color: #686b7e;
  font-size: .26rem;
  font-weight: 600;
}

.weak-list {
  display: grid;
  gap: .1rem;
}

.weak-item {
  padding: .14rem .16rem;
  background: #fff5f5;
  border: 1PX solid #f3dede;
  border-radius: .1rem;
}

.weak-item b {
  display: block;
  color: #c62828;
  font-size: .26rem;
  font-weight: 600;
}

.weak-item span {
  display: block;
  margin-top: .04rem;
  color: #777b8d;
  font-size: .23rem;
  line-height: 1.45;
}

.refresh-btn {
  margin-top: .22rem !important;
  height: .76rem !important;
  border-color: #efb8b8 !important;
  color: @color !important;
  background: #fff5f5 !important;
  border-radius: .12rem !important;
  font-size: .27rem !important;
}

.refresh-btn:active {
  background: #ffecec !important;
}

.cap, article {
  padding: .22rem 0;
  border-top: 1px solid #eee
}

.cap header {
  display: flex;
  justify-content: space-between;
  font-size: .28rem
}

.cap header span {
  color: #777
}

.cap > div {
  height: .12rem;
  margin: .14rem 0;
  background: #eee;
  border-radius: .08rem;
  overflow: hidden
}

.cap i {
  display: block;
  height: 100%;
  background: #c62828
}

.cap p, article p, article small {
  margin: .08rem 0;
  color: #777b8d;
  font-size: .25rem;
  line-height: 1.5
}

article em {
  float: right;
  color: #a32a2a;
  font-size: .23rem;
  font-style: normal
}
.growth-page { margin: 0; min-height: 100vh; background: #f6f7fb; padding-bottom: 1.7rem; }
.growth-page /deep/ .van-nav-bar { margin: 0; width: 100%; height: 1.22667rem; border-radius: 0; background: linear-gradient(135deg,#351b1d,#c6282d); }
</style>



// WEBPACK FOOTER //
// src/views/efficiency/Growth.vue